/**
 * Data for the Billing & Analytics page.
 *
 * Four things this has to get right:
 *
 * 1. **Billing must not break because analytics did.** The plan panel and the
 *    cost figures come from /billing; the template, status and job panels are
 *    borrowed from /dashboard. Those are secondary — each one keeps its own
 *    error, so a failing dashboard query costs you a panel, not the page.
 * 2. **Nothing is fetched for a tab nobody opened.** The template trend and the
 *    workload queries are the expensive ones, so they load on first use of the
 *    tab that needs them and then follow the period control. What does not
 *    depend on the period (top issuers, all-time job outcomes) is read once per
 *    refresh, not on every period change.
 * 3. **Late responses never win.** Every load carries a sequence number and a
 *    stale reply is dropped, so flicking through periods cannot leave the page
 *    showing an earlier period's numbers.
 * 4. **A number is never shown under a period it was not loaded for.** A failed
 *    reload keeps the previous figures on screen rather than blanking them, so
 *    every payload records `loadedFor` (see `periodKey`) and the page labels
 *    what it shows from that, not from the control.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { billingApi, dashboardApi, emailsApi } from '../../services/api'

/** The one period model the whole page shares. */
export const PERIODS = [
  { value: 'week', label: '7 days', days: 7 },
  { value: 'month', label: '30 days', days: 30 },
  { value: '3m', label: '3 months', days: 90 },
  { value: '6m', label: '6 months', days: 180 },
  { value: 'year', label: '12 months', days: 365 },
  // /billing/* go genuinely unbounded for this value. /dashboard/trend clamps
  // at 800 days server-side and says so (`truncated`), which the panels that
  // read it use to name their window honestly.
  { value: 'all', label: 'All time', days: 800 },
]

export const DEFAULT_PERIOD = '3m'

/** Months of history behind the tile sparklines, independent of the control. */
const HISTORY_MONTHS = 6

const pad2 = n => String(n).padStart(2, '0')
const isoDate = d => d.toISOString().slice(0, 10)

/**
 * The sparkline window as whole calendar months: the first day of the month
 * five months back through today. The buttons are rolling day counts, so the
 * "6 months" button starts mid-month and its first bucket would be partial.
 */
export function historyRange(now = new Date()) {
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (HISTORY_MONTHS - 1), 1))
  return { from: isoDate(from), to: isoDate(now) }
}

/** The calendar-month keys ("2026-05" ... "2026-10") the sparklines cover. */
export function historyMonthKeys(now = new Date()) {
  return Array.from({ length: HISTORY_MONTHS }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (HISTORY_MONTHS - 1 - i), 1))
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`
  })
}

/**
 * One string per period, the same for the same dates: 'week' ... 'all', or
 * 'custom|from|to'. Payloads are stamped with it when they load.
 */
export function periodKey(range, custom = null) {
  return custom?.from && custom?.to ? `custom|${custom.from}|${custom.to}` : range
}

/** Back from a key to { range, custom }. */
export function parsePeriodKey(key) {
  if (typeof key === 'string' && key.startsWith('custom|')) {
    const [, from, to] = key.split('|')
    return { range: 'custom', custom: { from, to } }
  }
  return { range: key || null, custom: null }
}

/** The label for a period key — what a payload loaded for that key should say. */
export function labelForKey(key) {
  if (!key) return null
  const { range, custom } = parsePeriodKey(key)
  return range === 'custom' ? formatCustomLabel(custom) : periodLabel(range)
}

export function periodDays(value) {
  // 'custom' has no fixed window — callers needing an actual span use the
  // {from, to} pair directly. This is only a safe fallback for call sites
  // that just need "long enough to not be a short-window special case".
  if (value === 'custom') return 999
  return (PERIODS.find(p => p.value === value) || PERIODS[2]).days
}

export function periodLabel(value) {
  return (PERIODS.find(p => p.value === value) || PERIODS[2]).label
}

/** True for the 'All time' period — call sites use this to drop the "Last "/"in the last " phrasing that only makes sense for a bounded window. */
export function isAllTimeLabel(label) {
  return label === 'All time'
}

/** "1 Jan 2026 – 31 Jan 2026" for a custom {from, to} (YYYY-MM-DD) pair. */
export function formatCustomLabel(custom) {
  if (!custom?.from || !custom?.to) return 'Custom range'
  const fmt = d => new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  return `${fmt(custom.from)} – ${fmt(custom.to)}`
}

/** A custom range is only usable once both ends are picked. */
export function isCustomRangeReady(custom) {
  return Boolean(custom?.from && custom?.to)
}

/**
 * The email endpoint takes a coarse label, not a date: only `week` (7 days)
 * and `month` (30) exist. Anything longer has no equivalent, so the parameter
 * is dropped and the panel says the figures are all-time rather than silently
 * showing a 30-day count under a 12-month heading.
 */
export function emailRangeParams(range) {
  const days = periodDays(range)
  if (days <= 7) return { time_range: 'week' }
  if (days <= 30) return { time_range: 'month' }
  return {}
}

export function emailRangeIsPeriod(range) {
  return periodDays(range) <= 30
}

function isoDaysAgo(days) {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - (days - 1))
  return d.toISOString().slice(0, 10)
}

/**
 * @param range one of the PERIODS values, or 'custom'
 * @param custom a { from, to } (YYYY-MM-DD) pair — only meaningful, and only
 *   read, when range === 'custom'. Requests wait for both ends to be picked.
 */
export function useBillingData(range, custom = null) {
  // `summaryFor` / `usageFor` / `historyFor` say which period each payload was
  // read for. A reload that fails leaves the payload (and its stamp) as it was.
  const [core, setCore] = useState({
    summary: null, usage: null, history: null, summaryFor: null, usageFor: null, historyFor: null,
  })
  const [coreState, setCoreState] = useState({ loading: true, refreshing: false, error: null })
  const [usageLoading, setUsageLoading] = useState(false)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [reloadErrors, setReloadErrors] = useState({ summary: null, usage: null })

  const [trend, setTrend] = useState({ data: null, issuers: null, loading: false, error: null, loadedFor: null })
  const [workload, setWorkload] = useState({ stats: null, jobs: null, emails: null, loading: false, error: null, loadedFor: null })

  // Which secondary sources have been asked for, so a period change refreshes
  // only what is actually on screen somewhere.
  const wanted = useRef({ trend: false, workload: false })
  const seq = useRef({ core: 0, usage: 0, summary: 0, trend: 0, workload: 0, history: 0 })

  const loadCore = useCallback(async ({ silent = false } = {}) => {
    const id = ++seq.current.core
    setCoreState(s => ({ ...s, loading: !silent && !core.summary, refreshing: silent, error: null }))
    try {
      const hist = historyRange()
      const key = periodKey(range, custom)
      const histKey = periodKey('custom', hist)
      const [summary, usage, history] = await Promise.all([
        billingApi.getSummary(range, custom),
        billingApi.getUsage(range, custom),
        // Not a second request when the period is the history window itself.
        key === histKey ? null : billingApi.getUsage('custom', hist),
      ])
      if (id !== seq.current.core) return
      setCore({
        summary: summary.data,
        usage: usage.data,
        history: (history || usage).data,
        summaryFor: key,
        usageFor: key,
        historyFor: histKey,
      })
      setCoreState({ loading: false, refreshing: false, error: null })
    } catch (err) {
      if (id !== seq.current.core) return
      setCoreState({ loading: false, refreshing: false, error: err })
    }
    // `range`/`custom` are read for the first load only; later period changes
    // go through loadUsage/loadSummary, which update in place rather than
    // re-running this. (The page never mounts with range === 'custom', so
    // this never fires against an incomplete custom range.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The sparkline window — whole calendar months, independent of the Analysis
  // Period control, so it has its own loader rather than riding along inside
  // loadCore (whose range/custom are frozen at mount and would otherwise make
  // Refresh re-fetch the *initial* period's usage/summary and clobber whatever
  // the current period just loaded).
  const loadHistory = useCallback(async (nextRange, nextCustom) => {
    const hist = historyRange()
    const histKey = periodKey('custom', hist)
    // The period call that runs alongside this one already reads the same window.
    if (periodKey(nextRange, nextCustom) === histKey) return
    const id = ++seq.current.history
    try {
      const res = await billingApi.getUsage('custom', hist)
      if (id !== seq.current.history) return
      setCore(c => ({ ...c, history: res.data, historyFor: histKey }))
    } catch {
      // Sparklines keep whatever history they last managed to load.
    }
  }, [])

  const loadUsage = useCallback(async (nextRange, nextCustom) => {
    const id = ++seq.current.usage
    const key = periodKey(nextRange, nextCustom)
    setUsageLoading(true)
    try {
      const res = await billingApi.getUsage(nextRange, nextCustom)
      if (id !== seq.current.usage) return
      const histKey = periodKey('custom', historyRange())
      setCore(c => ({
        ...c,
        usage: res.data,
        usageFor: key,
        // This response *is* the sparkline window when the period matches it.
        ...(key === histKey ? { history: res.data, historyFor: histKey } : {}),
      }))
      setReloadErrors(e => ({ ...e, usage: null }))
    } catch (err) {
      // The chart keeps the last period it managed to load (stamped with that
      // period, so it is labelled as such) rather than emptying.
      if (id === seq.current.usage) setReloadErrors(e => ({ ...e, usage: err }))
    } finally {
      if (id === seq.current.usage) setUsageLoading(false)
    }
  }, [])

  // The People/Costs tabs read `user_activity.period`, `group_activity.period`
  // and `estimated_costs.period` off the same summary object — scoped to
  // `nextRange`/`nextCustom` server-side — so a period change re-pulls it like
  // loadUsage does, without touching the cycle-scoped plan block that summary
  // also carries.
  const loadSummary = useCallback(async (nextRange, nextCustom) => {
    const id = ++seq.current.summary
    const key = periodKey(nextRange, nextCustom)
    setSummaryLoading(true)
    try {
      const res = await billingApi.getSummary(nextRange, nextCustom)
      if (id !== seq.current.summary) return
      setCore(c => ({ ...c, summary: res.data, summaryFor: key }))
      setReloadErrors(e => ({ ...e, summary: null }))
    } catch (err) {
      // Keep the last summary rather than blanking the plan panel; it keeps its
      // own period stamp, so its period-scoped figures are never relabelled.
      if (id === seq.current.summary) setReloadErrors(e => ({ ...e, summary: err }))
    } finally {
      if (id === seq.current.summary) setSummaryLoading(false)
    }
  }, [])

  /**
   * Template trend for the period. The issuer list is not period dependent, so
   * it is only read with `withIssuers` (first open and Refresh), never on a
   * period change.
   */
  const loadTrend = useCallback(async (nextRange, nextCustom, { withIssuers = false } = {}) => {
    wanted.current.trend = true
    const id = ++seq.current.trend
    const key = periodKey(nextRange, nextCustom)
    setTrend(t => ({ ...t, loading: true, error: null }))
    try {
      // The issuer list is a bonus panel on the same tab — it must not be able
      // to take the template table down with it.
      const [trendRes, issuerRes] = await Promise.allSettled([
        isCustomRangeReady(nextCustom)
          ? dashboardApi.getTrend(periodDays(nextRange), nextCustom.from, nextCustom.to)
          : dashboardApi.getTrend(periodDays(nextRange)),
        withIssuers ? dashboardApi.getTopIssuers(8) : Promise.resolve(null),
      ])
      if (id !== seq.current.trend) return
      setTrend(t => ({
        // A failed read keeps the previous data (and the period it was for).
        data: trendRes.status === 'fulfilled' ? trendRes.value.data : t.data,
        loadedFor: trendRes.status === 'fulfilled' ? key : t.loadedFor,
        issuers: !withIssuers
          ? t.issuers
          : issuerRes.status === 'fulfilled' ? (issuerRes.value?.data?.issuers || []) : null,
        loading: false,
        error: trendRes.status === 'rejected' ? trendRes.reason : null,
      }))
    } catch (err) {
      if (id !== seq.current.trend) return
      setTrend(t => ({ ...t, loading: false, error: err }))
    }
  }, [])

  /**
   * Request status mix and email intake for the period. Job outcomes are
   * organisation-wide and all-time, so they are only read with `withJobs`.
   */
  const loadWorkload = useCallback(async (nextRange, nextCustom, { withJobs = false } = {}) => {
    wanted.current.workload = true
    const id = ++seq.current.workload
    const key = periodKey(nextRange, nextCustom)
    setWorkload(w => ({ ...w, loading: true, error: null }))
    try {
      const ready = isCustomRangeReady(nextCustom)
      // /dashboard/stats takes the same window as the billing endpoints: the
      // last N whole UTC days including today, or unfiltered for 'all'.
      const statsParams = ready
        ? { date_from: nextCustom.from, date_to: nextCustom.to }
        : nextRange === 'all' ? {} : { date_from: isoDaysAgo(periodDays(nextRange)) }
      // Settled, not all: a failure in one should not also cost you the others.
      const [stats, jobs, emails] = await Promise.allSettled([
        dashboardApi.getStats(statsParams),
        withJobs ? dashboardApi.getJobStats() : Promise.resolve(null),
        // The email endpoint only understands week/month — a custom range has
        // no equivalent, so it falls back to all-time like any other long window.
        emailsApi.stats(ready ? {} : emailRangeParams(nextRange)),
      ])
      if (id !== seq.current.workload) return
      setWorkload(w => ({
        stats: stats.status === 'fulfilled' ? stats.value.data : w.stats,
        // Stats and emails are stamped together: one period, or the old one.
        loadedFor: stats.status === 'fulfilled' ? key : w.loadedFor,
        jobs: !withJobs ? w.jobs : jobs.status === 'fulfilled' ? jobs.value.data : null,
        emails: emails.status === 'fulfilled' ? emails.value.data : (stats.status === 'fulfilled' ? null : w.emails),
        loading: false,
        error: stats.status === 'rejected' && (!withJobs || jobs.status === 'rejected') ? stats.reason : null,
      }))
    } catch (err) {
      if (id !== seq.current.workload) return
      setWorkload(w => ({ ...w, loading: false, error: err }))
    }
  }, [])

  useEffect(() => { loadCore() }, [loadCore])

  // Period changes fan out to whatever has been loaded, and nothing else. A
  // 'custom' range with only one end picked is not yet fetchable — wait for both.
  const firstRun = useRef(true)
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return }
    if (range === 'custom' && !isCustomRangeReady(custom)) return
    loadUsage(range, custom)
    loadSummary(range, custom)
    if (wanted.current.trend) loadTrend(range, custom)
    if (wanted.current.workload) loadWorkload(range, custom)
  }, [range, custom, loadUsage, loadSummary, loadTrend, loadWorkload])

  const refresh = useCallback(() => {
    if (range === 'custom' && !isCustomRangeReady(custom)) return
    setCoreState(s => ({ ...s, refreshing: true }))
    Promise.allSettled([
      loadHistory(range, custom),
      loadUsage(range, custom),
      loadSummary(range, custom),
      wanted.current.trend ? loadTrend(range, custom, { withIssuers: true }) : null,
      wanted.current.workload ? loadWorkload(range, custom, { withJobs: true }) : null,
    ]).finally(() => setCoreState(s => ({ ...s, refreshing: false })))
  }, [range, custom, loadHistory, loadUsage, loadSummary, loadTrend, loadWorkload])

  const { summaryFor, usageFor, historyFor, ...payloads } = core
  return {
    ...payloads,
    ...coreState,
    usageLoading,
    summaryLoading,
    summaryError: reloadErrors.summary,
    usageError: reloadErrors.usage,
    // The period each payload was actually read for. Label from these.
    loadedFor: {
      summary: summaryFor,
      usage: usageFor,
      history: historyFor,
      trend: trend.loadedFor,
      workload: workload.loadedFor,
    },
    trend,
    workload,
    ensureTrend: () => { if (!wanted.current.trend) loadTrend(range, custom, { withIssuers: true }) },
    ensureWorkload: () => { if (!wanted.current.workload) loadWorkload(range, custom, { withJobs: true }) },
    refresh,
    retry: loadCore,
  }
}
